/**
 * Script Playback Engine
 * 
 * The core engine that reads funscript actions and sends them to devices
 * at the correct time. Handles playback speed, looping, seeking, and sync.
 */

import type { UserScript, FunscriptAction } from './script-store'
import { deviceController } from './device-controller'
import { useDeviceStore } from './device-store'
import { filterDeviceCommands } from './device-capabilities'
import { HandyDevice } from './devices/handy-device'
import { hashActions as hashActionsContent } from './funscript-hash'
import { positionAtTime } from './funscript-position'

// Playing a script with no device connected is an expected, common case (e.g. previewing/
// reviewing a delivery before bothering to connect a toy) — video/image playback works fine
// regardless, so this shouldn't be logged as an alarming console.error. Real engine failures
// still should be.
export function isNoDeviceError(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith('No device connected')
}

export interface PlaybackOptions {
  speed?: number // 0.25 - 2.0
  loop?: boolean
  startTime?: number // milliseconds
  syncOffset?: number // milliseconds (positive = delay, negative = advance)
  skipHSSP?: boolean // force HDSP per-command mode — instant, no upload needed (editors)
  /** Start prepared media and return its actual time before dispatching device playback. */
  beforeStart?: () => Promise<number | undefined>
}

export type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'stopped'

export interface PlaybackStats {
  actionsSent: number
  lastActionTime: number
  averageLatency: number
  missedActions: number
}

type TimeUpdateCallback = (time: number) => void
type ActionSentCallback = (action: FunscriptAction, index: number) => void
type CompleteCallback = () => void
type ErrorCallback = (error: Error) => void
type StateChangeCallback = (state: PlaybackState) => void

export class ScriptPlaybackEngine {
  private script: UserScript | null = null
  private actions: FunscriptAction[] = []
  private currentActionIndex: number = 0
  private playbackState: PlaybackState = 'idle'
  private playSequence = 0
  private previewPositionPending: number | null = null
  private previewPositionTimer: ReturnType<typeof setTimeout> | null = null
  private previewPositionInFlight: Promise<void> | null = null
  private previewPositionSequence = 0
  private previewPositionLastSentAt = -Infinity
  private hsspPausePromise: Promise<unknown> | null = null
  
  // Timing
  private startTime: number = 0
  private pauseTime: number = 0
  private pausedDuration: number = 0
  private playbackInterval: NodeJS.Timeout | null = null
  private readonly TICK_INTERVAL_MS = 50 // UI update interval only

  // Look-ahead scheduler (replaces poll-and-catch-up)
  private scheduledTimeouts: ReturnType<typeof setTimeout>[] = []
  private lastScheduledIndex: number = 0
  private readonly LOOKAHEAD_MS = 800 // Pre-schedule actions up to 800ms ahead

  // Simulated vibration for Handy — the device has no vibration motor (the
  // real HVP protocol needs an optional adapter accessory most Handys don't
  // have), so a Vibration (V0) curve is expressed instead as a fast small
  // jiggle layered on top of wherever the Stroke curve already is. Baked
  // into the action list once, right after actions load — both playback
  // paths (HSSP upload and the live HDSP loop) just see extra position
  // waypoints, no vibration-specific code needed in either.
  private readonly HANDY_VIBRATION_JIGGLE_INTERVAL_MS = 70 // ~14 alternations/sec
  private readonly HANDY_VIBRATION_MAX_AMPLITUDE = 8 // position points, at vibration=100
  
  // Options
  private speed: number = 1.0
  private loop: boolean = false
  private syncOffset: number = 0
  
  // HSSP state — used when device is Handy (WiFi) and script has been uploaded
  private hsspActive = false
  private hsspFilename: string | null = null
  private hsspPausePositionMs = 0
  private hsspResyncSequence = 0
  private hsspWatchdog: ReturnType<typeof setInterval> | null = null
  private hsspWatchdogMisses = 0
  private hsspLastRecoveryAt = 0
  private hsspWatchdogSequence = 0
  private hsspRecoveryInFlight = false
  // Track whether the Handy is currently in HSSP mode so we know
  // whether a resetToHDSPMode() call is needed before the next HDSP play.
  // Also track first-ever HDSP play so we reset once on cold connect.
  private handyNeedsHDSPReset = false
  private hdspInitDone = false

  // HSSP pre-warm: upload + clock-sync + device-setup run in the background
  // so pressing play only needs a single playHSSP call
  private prewarmPromise: Promise<void> | null = null
  private prewarmResult: { url: string; sha256: string; filename: string } | null = null
  private prewarmActionHash: string | null = null

  // Stats
  private stats: PlaybackStats = {
    actionsSent: 0,
    lastActionTime: 0,
    averageLatency: 0,
    missedActions: 0,
  }
  
  // Event callbacks
  private timeUpdateCallbacks: TimeUpdateCallback[] = []
  private actionSentCallbacks: ActionSentCallback[] = []
  private completeCallbacks: CompleteCallback[] = []
  private errorCallbacks: ErrorCallback[] = []
  private stateChangeCallbacks: StateChangeCallback[] = []
  
  /**
   * Play a script
   */
  async play(script: UserScript, options: PlaybackOptions = {}): Promise<void> {
    this.cancelPositionPreview()
    this.hsspResyncSequence++
    let sequence = 0
    try {
      // Cancel any pending rest-position command — if play is called quickly after stop()
      // (switching scripts, pressing play again) the rest-position move hasn't fired yet
      // and we don't want it to interfere with the start of new playback.
      if (this.restPositionTimer) {
        clearTimeout(this.restPositionTimer)
        this.restPositionTimer = null
      }

      // No device connected is an expected, common case (e.g. previewing/reviewing a
      // delivery before bothering to connect a toy) — video/image playback and the on-screen
      // timeline should still work. getHandyAdapter() below already returns null when nothing
      // is connected, which naturally skips every device-specific block further down and falls
      // through to the generic timer-driven path that sets playbackState to 'playing'.

      // Stop any existing playback (this arms a new restPositionTimer — cancel it immediately)
      if (this.playbackState === 'playing') {
        this.stop()
        if (this.restPositionTimer) {
          clearTimeout(this.restPositionTimer)
          this.restPositionTimer = null
        }
      }
      
      // Set script and options
      sequence = ++this.playSequence
      this.script = script
      this.speed = options.speed ?? 1.0
      this.loop = options.loop ?? false
      this.syncOffset = options.syncOffset ?? 0
      
      // Validate speed
      if (this.speed < 0.25 || this.speed > 2.0) {
        throw new Error('Playback speed must be between 0.25 and 2.0')
      }
      
      // Get and sort actions
      this.actions = this.buildPlaybackActions(script, !!this.getHandyAdapter())

      if (this.actions.length === 0) {
        throw new Error('Script has no actions to play')
      }

      // HDSP streaming: fill large gaps with interpolated intermediate points so the
      // Handy receives a continuous position stream rather than one long-duration command.
      // Without this, slow strokes (e.g. 2500ms gap) snap to target instead of gliding —
      // the Handy firmware caps per-command HDSP duration at roughly 300-600ms.
      if (options.skipHSSP) {
        this.actions = this.densifyActions(this.actions, 100)
      }
      
      // Set starting position
      let startTime = options.startTime ?? 0
      const alignMedia = async () => {
        const mediaTime = await options.beforeStart?.()
        if (mediaTime !== undefined && Number.isFinite(mediaTime)) startTime = Math.max(0, mediaTime)
        this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(startTime))
      }
      this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(startTime))

      // Reset stats (timing initialized per-path below, just before the loop starts)
      this.stats = {
        actionsSent: 0,
        lastActionTime: 0,
        averageLatency: 0,
        missedActions: 0,
      }
      
      const handy = this.getHandyAdapter()
      this.playbackState = 'loading'
      this.emitStateChange('loading')
      // Finish the last scrub/stop command before switching the device back to script playback.
      await this.previewPositionInFlight
      await this.hsspPausePromise
      if (sequence !== this.playSequence || this.playbackState !== 'loading') return
      if (handy && this.actions.length > 0 && !options.skipHSSP) {
        const actionHash = this.hashActions(this.actions)
        this.hsspActive = false

        // ── Fast path: device already has the script loaded (stop→play) ──────
        // Don't emit 'loading' — go straight to playing with just a playHSSP call.
        const canFastPlay = !!(this.prewarmResult && this.prewarmActionHash === actionHash && handy.isHSSPPrepared(this.prewarmResult.url))

        if (!canFastPlay) {
          // Slow path needs setup — show loading indicator
          this.hsspFilename = null
          try {
            // Wait for any in-flight prewarm first
            if (this.prewarmPromise && this.prewarmActionHash === actionHash) {
              console.log('[ScriptPlaybackEngine] Waiting for in-flight prewarm...')
              await this.prewarmPromise
            }
            if (sequence !== this.playSequence || this.playbackState !== 'loading') return

            let uploaded: { url: string; sha256: string; filename: string } | null = null
            let setupDone = false

            if (this.prewarmResult && this.prewarmActionHash === actionHash && handy.isHSSPPrepared(this.prewarmResult.url)) {
              // Prewarm finished while we were waiting
              uploaded = this.prewarmResult
              setupDone = true
            } else {
              const [prepResult, syncOk] = await Promise.all([
                handy.prepareHSSP(this.actions.map(a => ({ at: a.at, pos: a.pos }))),
                handy.syncServerClock(),
              ])
              if (sequence !== this.playSequence || this.playbackState !== 'loading') return
              uploaded = prepResult
              if (uploaded && syncOk) {
                setupDone = await handy.setupHSSP(uploaded.url, uploaded.sha256)
              }
            }
            if (sequence !== this.playSequence || this.playbackState !== 'loading') return

            if (uploaded && setupDone) {
              this.prewarmResult = { url: uploaded.url, sha256: uploaded.sha256, filename: uploaded.filename }
              this.prewarmActionHash = actionHash
              this.hsspFilename = uploaded.filename
            } else {
              // Setup failed — fall through to HDSP
              throw new Error('HSSP setup incomplete')
            }
          } catch (hsspErr) {
            if (sequence !== this.playSequence || this.playbackState !== 'loading') return
            console.warn('[ScriptPlaybackEngine] HSSP setup failed, falling back to HDSP:', hsspErr)
            await handy.resetToHDSPMode()
            if (sequence !== this.playSequence || this.playbackState !== 'loading') return
            // densifyActions was only applied on skipHSSP plays above — apply it here too so
            // slow-stroke sections (large gaps) glide instead of snap when falling back to HDSP.
            this.actions = this.densifyActions(this.actions, 100)
            await alignMedia()
            if (sequence !== this.playSequence || this.playbackState !== 'loading') return
            this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(startTime))
            this.startTime = performance.now() - (startTime / this.speed)
            this.pausedDuration = 0
            this.handyNeedsHDSPReset = false
            this.hdspInitDone = true
            this.playbackState = 'playing'
            this.emitStateChange('playing')
            this.startPlaybackLoop()
            return
          }
        }

        // ── Both paths converge here — device is ready, just call playHSSP ──
        try {
          // Re-sync clock for accurate timing (fast ~100ms, skipped on fast path to stay instant)
          if (!canFastPlay) {
            // Already synced above in slow path — nothing to do
          } else {
            // Fast path: refresh clock sync in background; don't await it so play is immediate
            handy.syncServerClock().catch(() => {})
          }

          await alignMedia()
          if (sequence !== this.playSequence || this.playbackState !== 'loading') return
          this.startTime = performance.now() - (startTime / this.speed)
          this.pausedDuration = 0
          const playOk = await handy.playHSSP(this.getDeviceScriptTime(startTime))

          if (sequence !== this.playSequence || this.playbackState !== 'loading') {
            if (this.getState() === 'stopped') await handy.stopHSSP()
            return
          }

          if (playOk) {
            if (this.playbackState === 'loading' || canFastPlay) {
              this.hsspActive = true
              this.handyNeedsHDSPReset = true  // device is now in HSSP mode
              this.hsspFilename = this.prewarmResult!.filename
              this.playbackState = 'playing'
              this.emitStateChange('playing')
              console.log(`[ScriptPlaybackEngine] HSSP playing (${canFastPlay ? 'instant' : 'after setup'})`)
              this.startUIOnlyInterval()
              this.startHSSPWatchdog()
              return
            }
          }
        } catch (playErr) {
          console.warn('[ScriptPlaybackEngine] playHSSP failed:', playErr)
        }

        // playHSSP failed — reset and fall through to HDSP
        if (sequence !== this.playSequence || this.playbackState !== 'loading') return
        await handy.resetToHDSPMode()
        this.actions = this.densifyActions(this.actions, 100)
      }

      // HDSP per-command path (non-Handy, skipHSSP, or HSSP failed)
      // For skipHSSP (editor) plays, always reset to HDSP mode — background prewarm may have
      // left the device in HSSP mode, which causes concurrent initializeHDSP() calls and
      // the "fighting/glitching" behavior. resetToHDSPMode() is a no-op if already in HDSP mode.
      const handyForReset = this.getHandyAdapter()
      if (handyForReset && (options.skipHSSP || this.handyNeedsHDSPReset || !this.hdspInitDone)) {
        await handyForReset.resetToHDSPMode().catch(() => {})
        if (sequence !== this.playSequence || this.playbackState !== 'loading') return
        this.handyNeedsHDSPReset = false
        this.hdspInitDone = true
      }
      // Set startTime here (after any awaits) so virtual clock starts accurately
      await alignMedia()
      if (sequence !== this.playSequence || this.playbackState !== 'loading') return
      this.startTime = performance.now() - (startTime / this.speed)
      this.pausedDuration = 0
      this.playbackState = 'playing'
      this.emitStateChange('playing')
      this.startPlaybackLoop()

      console.log('[ScriptPlaybackEngine] Started playback:', {
        scriptName: script.name,
        actionCount: this.actions.length,
        speed: this.speed,
        loop: this.loop,
        startTime,
      })
    } catch (error) {
      if (sequence === this.playSequence && this.getState() === 'loading') this.stop()
      if (!isNoDeviceError(error)) console.error('[ScriptPlaybackEngine] Play failed:', error)
      this.emitError(error as Error)
      throw error
    }
  }
  
  /**
   * Pause playback
   */
  pause(): void {
    if (this.playbackState === 'loading') {
      this.stop()
      return
    }
    if (this.playbackState !== 'playing') return
    this.hsspResyncSequence++

    this.pauseTime = performance.now()
    this.hsspPausePositionMs = this.getCurrentTime()
    this.playbackState = 'paused'
    this.emitStateChange('paused')

    if (this.hsspActive) {
      this.stopUIOnlyInterval()
      this.stopHSSPWatchdog()
      const handy = this.getHandyAdapter()
      if (handy) this.queueHSSPStop(handy)
    } else {
      this.stopPlaybackLoop()
      this.holdPositionAtTime(this.hsspPausePositionMs)
    }

    console.log('[ScriptPlaybackEngine] Paused at:', this.getCurrentTime())
  }
  
  /**
   * Resume playback
   */
  resume(): void {
    if (this.playbackState !== 'paused') return

    const handy = this.getHandyAdapter()
    if (this.hsspActive && handy) {
      if (this.script) {
        void this.play(this.script, { startTime: this.hsspPausePositionMs, speed: this.speed, loop: this.loop, syncOffset: this.syncOffset })
          .catch(err => console.error('[HSSP] resume after scrubbing failed:', err))
      }
      return
    }

    const pauseDuration = performance.now() - this.pauseTime
    this.pausedDuration += pauseDuration

    this.playbackState = 'playing'
    this.emitStateChange('playing')

    if (this.hsspActive) {
      const handy = this.getHandyAdapter()
      const offsetMs = this.getDeviceScriptTime(this.hsspPausePositionMs)
      handy?.playHSSP(offsetMs).catch(err => console.error('[HSSP] resume/playHSSP failed:', err))
      this.startUIOnlyInterval()
      this.startHSSPWatchdog()
    } else {
      this.startPlaybackLoop()
    }

    console.log('[ScriptPlaybackEngine] Resumed from:', this.getCurrentTime())
  }
  
  /**
   * Stop playback
   */
  stop(): void {
    this.cancelPositionPreview()
    this.hsspResyncSequence++
    this.playSequence++
    if (this.playbackState === 'idle' || this.playbackState === 'stopped') return

    const wasHssp = this.hsspActive
    const wasLoading = this.playbackState === 'loading'
    const stoppedScript = this.script

    if (this.hsspActive) {
      this.stopUIOnlyInterval()
      this.stopHSSPWatchdog()
      const handy = this.getHandyAdapter()
      // Don't cleanup the file — the device keeps the script in memory after stop (v2 state=3).
      // Next play can reuse the prepared script instead of downloading it again.
      // We prewarm immediately so the prewarmResult is ready before the user presses play.
      if (handy) this.queueHSSPStop(handy)
      this.hsspActive = false
      // Keep hsspFilename and prewarmResult — the prepared script remains loaded after stop.
      // Next play() will hit the fast path and skip upload+setup entirely.
    } else {
      this.stopPlaybackLoop()
    }

    this.playbackState = 'stopped'
    this.emitStateChange('stopped')

    // Send rest position only for HDSP (position-stream) mode, but delay it so that if
    // play() is called quickly after stop() (e.g. switching scripts, pressing play again)
    // the rest-position move is cancelled before it fires. Without this delay the 800ms
    // "move to zero" command would overlap the start of new playback, causing a visible glitch.
    if (!wasHssp && !wasLoading) {
      if (this.restPositionTimer) clearTimeout(this.restPositionTimer)
      this.restPositionTimer = setTimeout(() => {
        this.restPositionTimer = null
        const restPos = useDeviceStore.getState().getActiveDevice()?.settings.restPosition ?? 0
        deviceController.setPosition(restPos, 500, false).catch(err => {
          console.error('[ScriptPlaybackEngine] Failed to return to rest position:', err)
        })
      }, 400)
    }

    console.log('[ScriptPlaybackEngine] Stopped. Stats:', this.stats)
  }

  /**
   * Pre-warm HSSP: upload the script and prepare the device in the background.
   * Call this when a script is selected so that play() only needs a single playHSSP call.
   */
  async prewarm(script: UserScript): Promise<void> {
    const handy = this.getHandyAdapter()
    if (!handy) return

    // Expanded before hashing so this matches the hash play() computes from
    // this.actions (also expanded) — otherwise canFastPlay would never see
    // them as the same script and the pre-warm would go to waste.
    const actions = this.buildPlaybackActions(script, true)
    if (actions.length === 0) return

    const hash = this.hashActions(actions)

    // Already pre-warmed for this script and result is still fresh
    if (this.prewarmActionHash === hash && this.prewarmResult && handy.isHSSPPrepared(this.prewarmResult.url)) return

    // Avoid parallel prewarms for the same script
    if (this.prewarmPromise && this.prewarmActionHash === hash) return

    // Different script — clean up the old Supabase file so storage doesn't accumulate
    if (this.prewarmActionHash !== null && this.prewarmActionHash !== hash && this.hsspFilename) {
      handy.cleanupHSSP(this.hsspFilename).catch(() => {})
      this.hsspFilename = null
    }

    this.prewarmResult = null
    this.prewarmActionHash = hash

    const pendingPreview = this.previewPositionInFlight
    const pendingStop = this.hsspPausePromise
    this.prewarmPromise = (async () => {
      try {
        await pendingPreview
        await pendingStop
        console.log('[ScriptPlaybackEngine] Pre-warming HSSP...')
        const [uploaded, syncOk] = await Promise.all([
          handy.prepareHSSP(actions.map(a => ({ at: a.at, pos: a.pos }))),
          handy.syncServerClock(),
        ])
        if (uploaded && syncOk) {
          const setupOk = await handy.setupHSSP(uploaded.url, uploaded.sha256)
          // setupHSSP leaves device in HSSP mode — mark so play() knows to reset if needed
          this.handyNeedsHDSPReset = true
          if (setupOk && this.prewarmActionHash === hash) {
            this.prewarmResult = { url: uploaded.url, sha256: uploaded.sha256, filename: uploaded.filename }
            console.log('[ScriptPlaybackEngine] HSSP pre-warm complete — device ready')
          }
        }
      } catch (err) {
        console.warn('[ScriptPlaybackEngine] Prewarm failed:', err)
      } finally {
        this.prewarmPromise = null
      }
    })()

    return this.prewarmPromise
  }
  
  /**
   * Seek to a specific time
   */
  seek(timeMs: number): void {
    if (!this.script) return

    const wasPlaying = this.playbackState === 'playing'

    // Update local time tracking regardless of HSSP. When paused, reset the
    // pause anchor too; otherwise resume() would add the pre-seek paused time
    // again and the local clock would drift backward from the scrubbed point.
    this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(timeMs))
    this.lastScheduledIndex = this.currentActionIndex
    this.hsspPausePositionMs = timeMs
    if (this.playbackState === 'paused') this.pauseTime = performance.now()
    this.startTime = performance.now() - (timeMs / this.speed) - this.pausedDuration

    if (this.hsspActive) {
      // Stop current HSSP play then restart from new position
      const handy = this.getHandyAdapter()
      if (handy && wasPlaying) {
        const sequence = ++this.hsspResyncSequence
        this.hsspPausePromise = this.queueHSSPStop(handy).then(() => {
          if (sequence !== this.hsspResyncSequence || !this.hsspActive || this.getState() !== 'playing') return
          return handy.playHSSP(this.getDeviceScriptTime(timeMs))
        }).catch(err => console.error('[HSSP] seek failed:', err))
      }
    } else {
      this.clearScheduledTimeouts()
      if (wasPlaying) this.stopPlaybackLoop()

      if (wasPlaying) this.holdPositionAtTime(timeMs)
      else this.previewPosition(this.actions, timeMs)

      if (wasPlaying) this.startPlaybackLoop()
    }

    this.emitTimeUpdate(timeMs)
    console.log('[ScriptPlaybackEngine] Seeked to:', timeMs, 'ms')
  }
  
  /**
   * Lightweight video-sync correction.
   * Shifts startTime so getCurrentTime() === videoMs without restarting the loop.
   * Call on every video timeupdate during HDSP playback (~4× per second is fine).
   * No-ops in HSSP mode (device handles its own timing there).
   */
  syncToVideoTime(videoMs: number): void {
    if (this.playbackState !== 'playing' || this.hsspActive) return
    const clampedMs = Math.min(Math.max(0, videoMs), this.getDuration())
    const engineMs = this.getCurrentTime()
    const driftMs = clampedMs - engineMs

    // Video timeupdate events are frequent and noisy. Rebuilding the HDSP
    // look-ahead queue for every small correction can dispatch the same point
    // more than once, which the Handy feels as a jerk. Keep the existing queue
    // for ordinary sub-100ms drift and only rebuild it for a real seek/drift.
    if (Math.abs(driftMs) < 100) return

    // Shift startTime so getCurrentTime() returns clampedMs
    this.startTime = performance.now() - (clampedMs / this.speed) - this.pausedDuration
    // Re-index to the correct action position
    this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(clampedMs))
    this.lastScheduledIndex = this.currentActionIndex
    // Reschedule upcoming actions from the corrected position
    this.clearScheduledTimeouts()
    this.scheduleUpcomingActions()
  }

  /**
   * Set playback speed
   */
  setPlaybackSpeed(speed: number): void {
    if (speed < 0.25 || speed > 2.0) {
      console.warn('[ScriptPlaybackEngine] Speed must be between 0.25 and 2.0')
      return
    }
    
    const currentTime = this.getCurrentTime()
    this.speed = speed
    
    // Adjust start time to maintain current position
    this.startTime = performance.now() - (currentTime / this.speed) - this.pausedDuration
    
    console.log('[ScriptPlaybackEngine] Speed changed to:', speed)
  }
  
  /**
   * Set loop mode
   */
  setLoop(enabled: boolean): void {
    this.loop = enabled
    console.log('[ScriptPlaybackEngine] Loop:', enabled)
  }

  /** Follow authored positions while idle/paused. Serialize requests and keep only the latest drag sample. */
  previewPosition(actions: ReadonlyArray<{ at: number; pos: number }>, timeMs: number): void {
    if (this.playbackState === 'playing' || this.playbackState === 'loading') return
    const position = positionAtTime(actions, timeMs)
    if (position === null || !Number.isFinite(position)) return
    if (this.restPositionTimer) {
      clearTimeout(this.restPositionTimer)
      this.restPositionTimer = null
    }
    this.previewPositionPending = position
    this.schedulePositionPreview(Math.max(0, 100 - (performance.now() - this.previewPositionLastSentAt)))
  }

  private cancelPositionPreview(): void {
    this.previewPositionSequence++
    this.previewPositionPending = null
    if (this.previewPositionTimer) clearTimeout(this.previewPositionTimer)
    this.previewPositionTimer = null
  }

  private queueHSSPStop(handy: HandyDevice): Promise<unknown> {
    // A cloud stop must finish before another seek/play can restart the device.
    const pending = this.hsspPausePromise
    const stop = Promise.resolve(pending).catch(() => {}).then(() => handy.stopHSSP())
      .catch(err => console.error('[HSSP] stop failed:', err))
    this.hsspPausePromise = stop
    return stop
  }

  private schedulePositionPreview(delayMs: number): void {
    if (this.previewPositionTimer || this.previewPositionInFlight || this.previewPositionPending === null) return
    this.previewPositionTimer = setTimeout(() => {
      this.previewPositionTimer = null
      const sequence = this.previewPositionSequence
      const startedAt = performance.now()
      this.previewPositionInFlight = (async () => {
        await this.prewarmPromise
        await this.hsspPausePromise
        if (sequence !== this.previewPositionSequence || this.getState() === 'playing' || this.getState() === 'loading') return
        const position = this.previewPositionPending
        this.previewPositionPending = null
        if (position === null) return
        this.previewPositionLastSentAt = performance.now()
        const handy = this.getHandyAdapter()
        if (handy) await handy.holdPosition(position)
        else await deviceController.setPosition(position, 50, true)
      })().catch(error => {
        if (!isNoDeviceError(error)) console.error('[ScriptPlaybackEngine] Scrub preview failed:', error)
      }).finally(() => {
        this.previewPositionInFlight = null
        this.schedulePositionPreview(Math.max(0, 100 - (performance.now() - startedAt)))
      })
    }, delayMs)
  }

  /**
   * Set sync offset
   */
  setSyncOffset(ms: number): void {
    this.syncOffset = ms
    if (this.playbackState === 'playing' && this.hsspActive) {
      const handy = this.getHandyAdapter()
      const currentTime = this.getCurrentTime()
      const sequence = ++this.hsspResyncSequence
      if (handy) this.hsspPausePromise = this.queueHSSPStop(handy)
        .then(() => {
          if (sequence !== this.hsspResyncSequence || this.playbackState !== 'playing' || !this.hsspActive) return false
          return handy.playHSSP(this.getDeviceScriptTime(currentTime))
        })
        .catch(err => console.error('[HSSP] sync offset update failed:', err))
    } else if (this.playbackState === 'playing') {
      this.currentActionIndex = this.findActionIndexAtTime(this.getDeviceScriptTime(this.getCurrentTime()))
      this.clearScheduledTimeouts()
      this.scheduleUpcomingActions()
    }
    console.log('[ScriptPlaybackEngine] Sync offset:', ms, 'ms')
  }
  
  /**
   * Get current playback time in milliseconds
   */
  getCurrentTime(): number {
    if (this.playbackState === 'idle' || this.playbackState === 'stopped') {
      return 0
    }
    
    if (this.playbackState === 'paused') {
      return ((this.pauseTime - this.startTime - this.pausedDuration) * this.speed)
    }
    
    return ((performance.now() - this.startTime - this.pausedDuration) * this.speed)
  }
  
  /**
   * Get total duration in milliseconds
   */
  getDuration(): number {
    if (!this.script || this.actions.length === 0) return 0
    return this.actions[this.actions.length - 1].at
  }
  
  /**
   * Get playback progress (0-1)
   */
  getProgress(): number {
    const duration = this.getDuration()
    if (duration === 0) return 0
    return Math.min(1, this.getCurrentTime() / duration)
  }
  
  /**
   * Check if playing
   */
  getIsPlaying(): boolean {
    return this.playbackState === 'playing'
  }

  getPlaybackState(): PlaybackState {
    return this.playbackState
  }
  
  /**
   * Get current state
   */
  getState(): PlaybackState {
    return this.playbackState
  }
  
  /**
   * Get playback stats
   */
  getStats(): PlaybackStats {
    return { ...this.stats }
  }
  
  // Event listeners
  
  onTimeUpdate(callback: TimeUpdateCallback): () => void {
    this.timeUpdateCallbacks.push(callback)
    return () => {
      this.timeUpdateCallbacks = this.timeUpdateCallbacks.filter(cb => cb !== callback)
    }
  }
  
  onActionSent(callback: ActionSentCallback): () => void {
    this.actionSentCallbacks.push(callback)
    return () => {
      this.actionSentCallbacks = this.actionSentCallbacks.filter(cb => cb !== callback)
    }
  }
  
  onComplete(callback: CompleteCallback): () => void {
    this.completeCallbacks.push(callback)
    return () => {
      this.completeCallbacks = this.completeCallbacks.filter(cb => cb !== callback)
    }
  }
  
  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback)
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter(cb => cb !== callback)
    }
  }
  
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeCallbacks.push(callback)
    return () => {
      this.stateChangeCallbacks = this.stateChangeCallbacks.filter(cb => cb !== callback)
    }
  }
  
  /**
   * Cleanup - call when engine is no longer needed
   */
  destroy(): void {
    // Cleanup Supabase file on engine teardown
    const handy = this.getHandyAdapter()
    if (handy && this.hsspFilename) handy.cleanupHSSP(this.hsspFilename).catch(() => {})
    this.stop()
    this.stopUIOnlyInterval()
    this.timeUpdateCallbacks = []
    this.actionSentCallbacks = []
    this.completeCallbacks = []
    this.errorCallbacks = []
    this.stateChangeCallbacks = []
    this.script = null
    this.actions = []
  }
  
  // Private methods
  
  private startPlaybackLoop(): void {
    this.clearScheduledTimeouts()
    this.lastScheduledIndex = this.currentActionIndex
    this.scheduleUpcomingActions()

    if (this.playbackInterval) clearInterval(this.playbackInterval)

    // Interval is for UI updates + scheduling new batches only — not action dispatch
    this.playbackInterval = setInterval(() => {
      if (this.playbackState !== 'playing') return
      const currentTime = this.getCurrentTime()
      this.emitTimeUpdate(currentTime)
      this.scheduleUpcomingActions()

      if (currentTime >= this.getDuration()) {
        if (this.loop) {
          this.seek(0)
        } else {
          this.stop()
          this.emitComplete()
        }
      }
    }, this.TICK_INTERVAL_MS)
  }

  private stopPlaybackLoop(): void {
    if (this.playbackInterval) {
      clearInterval(this.playbackInterval)
      this.playbackInterval = null
    }
    this.clearScheduledTimeouts()
  }

  // Pre-schedule the next LOOKAHEAD_MS worth of actions using setTimeout for precise timing.
  // Each action fires at its exact timestamp rather than being caught in a 50ms batch —
  // this prevents the debounce overwrite bug that drops closely-packed actions.
  private scheduleUpcomingActions(): void {
    if (this.playbackState !== 'playing') return

    const nowReal = performance.now()
    const rawCurrentTime = this.getCurrentTime()
    const lookAheadEndMs = this.getDeviceScriptTime(rawCurrentTime) + this.LOOKAHEAD_MS

    while (this.lastScheduledIndex < this.actions.length) {
      const action = this.actions[this.lastScheduledIndex]
      if (action.at > lookAheadEndMs) break

      const actionRealTime = this.startTime + this.pausedDuration + ((action.at + this.syncOffset) / this.speed)
      const delayMs = Math.max(0, actionRealTime - nowReal)

      const act = action
      const idx = this.lastScheduledIndex
      const t = setTimeout(() => {
        if (this.playbackState === 'playing') this.sendAction(act, idx)
      }, delayMs)

      this.scheduledTimeouts.push(t)
      this.lastScheduledIndex++
    }
  }

  private clearScheduledTimeouts(): void {
    for (const t of this.scheduledTimeouts) clearTimeout(t)
    this.scheduledTimeouts = []
    this.lastScheduledIndex = this.currentActionIndex
  }

  private holdPositionAtTime(timeMs: number): void {
    const position = this.getInterpolatedPositionAtTime(this.getDeviceScriptTime(timeMs))
    if (position === null) return
    const handy = this.getHandyAdapter()
    if (handy) {
      handy.holdPosition(position).catch(err => {
        console.error('[ScriptPlaybackEngine] Pause hold position update failed:', err)
      })
      return
    }
    deviceController.setPosition(position, 50, true).catch(err => {
      console.error('[ScriptPlaybackEngine] Pause hold position update failed:', err)
    })
  }

  private getInterpolatedPositionAtTime(timeMs: number): number | null {
    if (this.actions.length === 0) return null
    const index = this.findActionIndexAtTime(timeMs)
    const current = this.actions[index]
    const next = this.actions[index + 1]
    if (!next || timeMs <= current.at) return current.pos
    const span = next.at - current.at
    if (span <= 0) return current.pos
    const t = Math.max(0, Math.min(1, (timeMs - current.at) / span))
    return Math.round(current.pos + (next.pos - current.pos) * t)
  }

  private getDeviceScriptTime(videoTimeMs: number): number {
    // Positive offset delays device movement; negative offset sends commands earlier.
    return Math.max(0, videoTimeMs - this.syncOffset)
  }

  private getScriptMetadataDuration(script: UserScript): number {
    const raw = script.funscript.metadata?.duration ?? 0
    if (!Number.isFinite(raw) || raw <= 0) return 0
    const lastAt = script.funscript.actions.reduce((last, action) => Math.max(last, action.at), 0)
    // Short editor scripts already use milliseconds. Compare against their action timestamps
    // before treating a small imported duration as seconds (3000 ms must not become 50 minutes).
    return raw < 7200 && (lastAt === 0 || raw < lastAt) ? raw * 1000 : raw
  }

  private buildPlaybackActions(script: UserScript, handy: boolean): FunscriptAction[] {
    const actions = [...script.funscript.actions].sort((a, b) => a.at - b.at)
    const last = actions[actions.length - 1]
    const duration = this.getScriptMetadataDuration(script)
    if (last && duration > last.at) actions.push({ ...last, at: duration })
    return handy ? this.expandHandyVibrationJiggle(actions) : actions
  }
  
  private async sendAction(action: FunscriptAction, index: number): Promise<void> {
    try {
      const sendStartTime = performance.now()
      
      // Calculate duration to next action
      let duration = 100 // Default 100ms
      if (index < this.actions.length - 1) {
        const nextAction = this.actions[index + 1]
        duration = Math.max(50, Math.min(30000, (nextAction.at - action.at) / this.speed))
      }

      // Get active device type for capability filtering
      const activeDevice = useDeviceStore.getState().getActiveDevice()
      const deviceType = activeDevice?.type ?? 'handy'

      // Build full command from all axis fields in the action
      const rawCommand = {
        position: action.pos,
        vibration: action.vibration,
        rotation: action.rotation,
        oscillation: action.oscillation,
        air: action.air,
        squeeze: action.air,
        surge: action.surge,
        sway: action.sway,
        roll: action.roll,
        pitch: action.pitch,
      }

      // Strip commands the device doesn't support
      const filtered = filterDeviceCommands(deviceType, rawCommand, activeDevice?.apiVersion)

      const sends: Promise<boolean>[] = []

      if (filtered.position !== undefined) {
        sends.push(deviceController.setPosition(filtered.position, duration, false))
      }
      if (filtered.vibration !== undefined) {
        sends.push(deviceController.setVibration(filtered.vibration))
      }
      if (filtered.rotation !== undefined) {
        sends.push(deviceController.setRotation(filtered.rotation))
      }
      if (filtered.oscillation !== undefined) {
        sends.push(deviceController.setOscillation(filtered.oscillation))
      }
      if (filtered.squeeze !== undefined) {
        sends.push(deviceController.setConstrict(filtered.squeeze))
      }
      if (filtered.surge !== undefined) {
        sends.push(deviceController.setSurge(filtered.surge, duration))
      }
      if (filtered.sway !== undefined) {
        sends.push(deviceController.setSway(filtered.sway, duration))
      }
      if (filtered.roll !== undefined) {
        sends.push(deviceController.setRoll(filtered.roll, duration))
      }
      if (filtered.pitch !== undefined) {
        sends.push(deviceController.setPitch(filtered.pitch, duration))
      }

      await Promise.all(sends)

      // Update stats
      const latency = performance.now() - sendStartTime
      this.stats.actionsSent++
      this.stats.lastActionTime = action.at
      this.stats.averageLatency =
        (this.stats.averageLatency * (this.stats.actionsSent - 1) + latency) / this.stats.actionsSent

      this.emitActionSent(action, index)

    } catch (error) {
      // Not a real failure — there was simply nothing to send to. This is the expected
      // path when previewing a script without a device connected (see isNoDeviceError).
      if (error instanceof Error && error.message.includes('No active device')) return
      console.error('[ScriptPlaybackEngine] Failed to send action:', error)
      this.stats.missedActions++
      this.emitError(error as Error)
    }
  }
  
  private hashActions(actions: Array<{ at: number; pos: number }>): string {
    return hashActionsContent(actions)
  }

  /** Returns the HandyDevice adapter if the active device is a WiFi Handy, else null */
  private getHandyAdapter(): HandyDevice | null {
    const adapter = deviceController.getCurrentAdapter()
    return adapter instanceof HandyDevice ? adapter : null
  }

  /** Delayed rest-position timer — cancelled if play() is called quickly after stop() */
  private restPositionTimer: ReturnType<typeof setTimeout> | null = null

  /** Interval for UI time updates when HSSP is active (no action scheduling needed) */
  private uiOnlyInterval: ReturnType<typeof setInterval> | null = null

  private startUIOnlyInterval(): void {
    if (this.uiOnlyInterval) clearInterval(this.uiOnlyInterval)
    this.uiOnlyInterval = setInterval(() => {
      if (this.playbackState !== 'playing') return
      const currentTime = this.getCurrentTime()
      this.emitTimeUpdate(currentTime)
      if (currentTime >= this.getDuration()) {
        if (this.loop) {
          this.seek(0)
          return
        }
        this.hsspActive = false
        this.stopUIOnlyInterval()
        this.stopHSSPWatchdog()
        this.playbackState = 'stopped'
        this.emitStateChange('stopped')
        this.emitComplete()
      }
    }, this.TICK_INTERVAL_MS)
  }

  private stopUIOnlyInterval(): void {
    if (this.uiOnlyInterval) {
      clearInterval(this.uiOnlyInterval)
      this.uiOnlyInterval = null
    }
  }

  private startHSSPWatchdog(): void {
    this.stopHSSPWatchdog()
    const sequence = this.hsspWatchdogSequence
    this.hsspWatchdogMisses = 0
    this.hsspWatchdog = setInterval(() => {
      if (!this.hsspActive || this.playbackState !== 'playing') return
      const handy = this.getHandyAdapter()
      if (!handy || this.getCurrentTime() >= this.getDuration()) return

      handy.getHSSPPlaybackState().then((state) => {
        if (sequence !== this.hsspWatchdogSequence || !this.hsspActive || this.playbackState !== 'playing') return
        if (state === 'playing' || state === 'unknown') {
          this.hsspWatchdogMisses = 0
          return
        }
        this.hsspWatchdogMisses += 1
        if (this.hsspWatchdogMisses < 2) return
        // A status request can arrive as the device naturally finishes. Do not replay its last stroke.
        if (this.getDuration() - this.getCurrentTime() <= 1000 * this.speed || this.hsspRecoveryInFlight) return
        if (performance.now() - this.hsspLastRecoveryAt < 3000) return
        this.hsspWatchdogMisses = 0
        this.hsspLastRecoveryAt = performance.now()
        const offsetMs = this.getDeviceScriptTime(this.getCurrentTime())
        console.warn('[ScriptPlaybackEngine] Handy HSSP left playing state; resuming at', offsetMs, 'ms')
        this.hsspRecoveryInFlight = true
        handy.playHSSP(offsetMs).then(async () => {
          if (sequence !== this.hsspWatchdogSequence && (this.getState() === 'stopped' || this.getState() === 'paused')) {
            await handy.stopHSSP()
          }
        }).catch((err) => console.error('[HSSP] watchdog resume failed:', err))
          .finally(() => {
            if (sequence === this.hsspWatchdogSequence) this.hsspRecoveryInFlight = false
          })
      }).catch(() => {})
    }, 1000)
  }

  private stopHSSPWatchdog(): void {
    this.hsspWatchdogSequence++
    this.hsspRecoveryInFlight = false
    if (this.hsspWatchdog) {
      clearInterval(this.hsspWatchdog)
      this.hsspWatchdog = null
    }
    this.hsspWatchdogMisses = 0
  }

  // Inserts interpolated intermediate points for gaps larger than GAP_THRESHOLD so the
  // Handy receives a continuous stream of small commands in HDSP mode. Without this,
  // sending one 2500ms command to the Handy results in a snap-to-target because the
  // firmware has an internal duration cap (~300-600ms).
  private densifyActions(actions: FunscriptAction[], intervalMs: number): FunscriptAction[] {
    const GAP_THRESHOLD = 350
    const filled: FunscriptAction[] = []
    for (let i = 0; i < actions.length; i++) {
      filled.push(actions[i])
      if (i < actions.length - 1) {
        const curr = actions[i]
        const next = actions[i + 1]
        const gap = next.at - curr.at
        if (gap > GAP_THRESHOLD) {
          const steps = Math.floor(gap / intervalMs)
          for (let s = 1; s < steps; s++) {
            const t = curr.at + gap * s / steps
            const pos = Math.round(curr.pos + (next.pos - curr.pos) * s / steps)
            filled.push({ at: Math.round(t), pos })
          }
        }
      }
    }
    return filled
  }

  // Expands any stretch where the authored Vibration curve is > 0 into a
  // dense run of small alternating position points around the Stroke
  // curve's own value at that instant — "jiggle wherever it already is,"
  // not a frozen buzz-in-place. Segments with no vibration on either end
  // pass through untouched. Strips the vibration field from every action
  // afterward (both untouched and newly inserted) so the live-HDSP fallback
  // path's separate `deviceController.setVibration()` call never fires for
  // Handy — the effect is now fully expressed as position, not HVP.
  private expandHandyVibrationJiggle(actions: FunscriptAction[]): FunscriptAction[] {
    const intervalMs = this.HANDY_VIBRATION_JIGGLE_INTERVAL_MS
    const maxAmplitude = this.HANDY_VIBRATION_MAX_AMPLITUDE
    const expanded: FunscriptAction[] = []

    for (let i = 0; i < actions.length; i++) {
      const { vibration, ...curr } = actions[i]
      expanded.push(curr)

      if (i < actions.length - 1) {
        const next = actions[i + 1]
        const gap = next.at - actions[i].at
        const vibStart = actions[i].vibration ?? 0
        const vibEnd = next.vibration ?? 0
        if (gap > intervalMs && (vibStart > 0 || vibEnd > 0)) {
          const steps = Math.floor(gap / intervalMs)
          for (let s = 1; s < steps; s++) {
            const t = s / steps
            const basePos = actions[i].pos + (next.pos - actions[i].pos) * t
            const vib = vibStart + (vibEnd - vibStart) * t
            const amplitude = (vib / 100) * maxAmplitude
            const offset = s % 2 === 0 ? amplitude : -amplitude
            const pos = Math.max(0, Math.min(100, Math.round(basePos + offset)))
            expanded.push({ at: Math.round(actions[i].at + gap * t), pos })
          }
        }
      }
    }
    return expanded
  }

  private findActionIndexAtTime(timeMs: number): number {
    // Binary search for efficiency
    let left = 0
    let right = this.actions.length - 1
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const action = this.actions[mid]
      
      if (action.at === timeMs) {
        return mid
      } else if (action.at < timeMs) {
        left = mid + 1
      } else {
        right = mid - 1
      }
    }
    
    // Return the closest action before or at the time
    return Math.max(0, Math.min(right, this.actions.length - 1))
  }
  
  // Event emitters
  
  private emitTimeUpdate(time: number): void {
    this.timeUpdateCallbacks.forEach(cb => {
      try {
        cb(time)
      } catch (error) {
        console.error('[ScriptPlaybackEngine] Time update callback error:', error)
      }
    })
  }
  
  private emitActionSent(action: FunscriptAction, index: number): void {
    this.actionSentCallbacks.forEach(cb => {
      try {
        cb(action, index)
      } catch (error) {
        console.error('[ScriptPlaybackEngine] Action sent callback error:', error)
      }
    })
  }
  
  private emitComplete(): void {
    this.completeCallbacks.forEach(cb => {
      try {
        cb()
      } catch (error) {
        console.error('[ScriptPlaybackEngine] Complete callback error:', error)
      }
    })
  }
  
  private emitError(error: Error): void {
    this.errorCallbacks.forEach(cb => {
      try {
        cb(error)
      } catch (error) {
        console.error('[ScriptPlaybackEngine] Error callback error:', error)
      }
    })
  }
  
  private emitStateChange(state: PlaybackState): void {
    this.stateChangeCallbacks.forEach(cb => {
      try {
        cb(state)
      } catch (error) {
        console.error('[ScriptPlaybackEngine] State change callback error:', error)
      }
    })
  }
}
